import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Button } from '../components/ui';
import { acceptAnalytics, declineAnalytics } from './analytics';
import { useAnalytics } from './useAnalytics';

/** CSS-Variable mit der Höhe des Banners: Die Seite bekommt unten so viel Platz, nichts bleibt verdeckt. */
const SPACE_VAR = '--consent-banner-space';

/**
 * Einwilligungsbanner für Google Analytics. Erscheint nur, wenn GA eingeschaltet ist (Mess-ID vom Server), die Seite im
 * Browser läuft (nicht in der App), der Browser kein „Do Not Track“/„Global Privacy Control“ sendet und noch keine
 * Entscheidung vorliegt. „Ablehnen“ und „Annehmen“ sind gleich gestaltet und gleich leicht erreichbar. Das Banner
 * sperrt die Seite nicht (keine Fokusfalle): Die Seite bekommt unten Platz in Höhe des Banners, damit Fußzeile
 * (Impressum, Datenschutz) und fokussierte Elemente nicht verdeckt werden (WCAG 2.4.11). Nach der Entscheidung geht
 * der Fokus auf den Hauptinhalt statt verloren.
 */
export function ConsentBanner() {
  const { needsChoice } = useAnalytics();
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!needsChoice || node === null) return undefined;
    const root = document.documentElement;
    const update = (): void => {
      root.style.setProperty(
        SPACE_VAR,
        `${String(Math.ceil(node.getBoundingClientRect().height))}px`,
      );
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(SPACE_VAR);
    };
  }, [needsChoice]);

  if (!needsChoice) return null;

  const choose = (action: () => void) => () => {
    const hadFocus = ref.current?.contains(document.activeElement) ?? false;
    action();
    if (hadFocus) document.getElementById('inhalt')?.focus({ preventScroll: true });
  };

  return (
    <section ref={ref} className="consent-banner" role="region" aria-labelledby="consent-title">
      <h2 id="consent-title" className="consent-banner__title">
        Reichweitenmessung mit Google Analytics
      </h2>
      <p>
        Mit Ihrer Einwilligung messen wir mit Google Analytics, welche Seiten genutzt werden. Dabei
        werden Cookies gesetzt und Daten an Google übertragen, auch in die USA; Ihre IP-Adresse
        erhält Google dabei technisch bedingt. Ohne Werbefunktionen. Ihre Einwilligung ist
        freiwillig und jederzeit unter „Datenschutz-Einstellungen“ widerrufbar. Mehr in der{' '}
        <Link to="/datenschutz#google-analytics">Datenschutzerklärung</Link>.
      </p>
      <div className="consent-banner__actions">
        <Button icon="cross" onClick={choose(declineAnalytics)}>
          Ablehnen
        </Button>
        <Button icon="check" onClick={choose(acceptAnalytics)}>
          Annehmen
        </Button>
      </div>
    </section>
  );
}
